// yonlendirme.jsx (güncellenmiş)
import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Gitarlar from "./Gitarlar";
import GitarDetay from "./GitarDetay";
import ScrollWithSpinner from "./ScrollWithSpinner";
import App from "../App";
import NotFound404 from "./NotFound404";
import Siparis from "./Siparis";
import Hakkimizda from "./Hakkimizda";
import AdminLogin from "./AdminLogin";
import AdminPanel from "./AdminPanel";
import AdminRoute from "./AdminRoute";

// Domain Redirect
function DomainRedirect() {
  useEffect(() => {
    if (
      window.location.hostname === "neoguitarstore-a951b.web.app" ||
      window.location.hostname === "neoguitarstore-a951b.firebaseapp.com"
    ) {
      window.location.href = "https://neoguitarstore.com";
    }
  }, []);
  return null;
}

function MetaTagManager() {
  const location = useLocation();
  
  useEffect(() => {
    const path = location.pathname;
    let title = "Neo Guitar Store - Premium Elektro Gitarlar";
    let description = "Profesyonel elektro gitarlar, bas gitarlar ve aksesuarlar.";
    let image = "/logo.png";
    let url = window.location.href;
    
    // Meta tag'leri path'e göre güncelle
    if (path.startsWith("/gitar/")) {
      title = "Gitar Detayları - Neo Guitar Store";
      description = "Premium kalitede gitar detaylarını inceleyin.";
      image = "/default-guitar.jpg";
    } else if (path === "/gitarlar") {
      title = "Gitarlar - Neo Guitar Store";
      description = "Geniş gitar koleksiyonumuzu keşfedin.";
    } else if (path === "/siparis") {
      title = "Custom Gitar & Takas - Neo Guitar Store";
      description = "Custom gitar siparişi verin veya elektro gitar takası yapın.";
    } else if (path === "/hakkimizda") {
      title = "Hakkımızda - Neo Guitar Store";
      description = "İstanbul Üsküdar'da elektro gitar tutkusunu ustalıkla birleştiren özel gitar mağazası.";
    } else if (path.startsWith("/admin")) {
      title = "Admin Paneli - Neo Guitar Store";
      description = "Neo Guitar Store yönetim paneli";
    }
    
    document.title = title;
    
    // Description meta tag'ini güncelle
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;
    
    // Open Graph ve Twitter meta tag'lerini güncelle
    const ogTags = {
      'og:title': title,
      'og:description': description,
      'og:image': image,
      'og:url': url,
      'og:type': 'website',
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image
    };
    
    Object.entries(ogTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || 
                 document.querySelector(`meta[name="${property}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property.startsWith('og:')) {
          meta.setAttribute('property', property);
        } else {
          meta.setAttribute('name', property);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    });
    
    // Hakkımızda sayfası için schema.org yapısal veri
    if (path === "/hakkimizda") {
      const localBusinessSchema = {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        'name': 'Neo Guitar Store',
        'description': 'İstanbul Üsküdar\'da elektro gitar tutkusunu ustalıkla birleştiren özel gitar mağazası.',
        'url': window.location.origin,
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Üsküdar',
          'addressRegion': 'İstanbul',
          'addressCountry': 'TR'
        },
        'image': '/logo.png',
        'telephone': '+90 530 864 8486',
        'priceRange': '₺₺₺',
        'openingHours': 'Mo-Sa 10:00-19:00',
        'sameAs': [
          'https://instagram.com/neoguitarstore',
          'https://facebook.com/neoguitarstore'
        ],
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'Elektro Gitarlar',
          'itemListElement': [
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Product',
                'name': 'Custom Elektro Gitar'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Product',
                'name': 'Elektro Gitar Takas'
              }
            }
          ]
        }
      };
      
      // Eski schema script'ini temizle
      const oldSchemaScript = document.getElementById('local-business-schema');
      if (oldSchemaScript) {
        oldSchemaScript.remove();
      }
      
      // Yeni schema script'ini ekle
      const script = document.createElement('script');
      script.id = 'local-business-schema';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(localBusinessSchema);
      document.head.appendChild(script);
      
      return () => {
        const schemaScript = document.getElementById('local-business-schema');
        if (schemaScript) schemaScript.remove();
      };
    }
  }, [location]);
  
  return null;
}

function DynamicMetaTags({ guitar }) {
  useEffect(() => {
    if (!guitar) return;
    
    const title = `${guitar.name} - ${guitar.brand || ''} | Neo Guitar Store`;
    const description = guitar.description 
      ? guitar.description.substring(0, 160) 
      : `Kaliteli ${guitar.name} gitarını inceleyin.`;
    const image = guitar.imageUrls?.[0] || guitar.imageUrl || '/default-guitar.jpg';
    const url = window.location.href;
    
    document.title = title;
    
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.content = description;
    }
    
    const metaTags = {
      'og:title': title,
      'og:description': description,
      'og:image': image,
      'og:url': url,
      'og:type': 'product',
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      'twitter:image': image
    };
    
    Object.entries(metaTags).forEach(([property, content]) => {
      let meta = document.querySelector(`meta[property="${property}"]`) || 
                 document.querySelector(`meta[name="${property}"]`);
      if (meta) {
        meta.setAttribute('content', content);
      }
    });
    
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': 'Ana Sayfa',
          'item': window.location.origin
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': 'Gitarlar',
          'item': `${window.location.origin}/gitarlar`
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': guitar.name,
          'item': url
        }
      ]
    };
    
    const oldSchemaScript = document.getElementById('breadcrumb-schema');
    if (oldSchemaScript) {
      oldSchemaScript.remove();
    }
    
    const script = document.createElement('script');
    script.id = 'breadcrumb-schema';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script);
    
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      'name': guitar.name,
      'description': guitar.description,
      'image': image,
      'brand': {
        '@type': 'Brand',
        'name': guitar.brand
      },
      'offers': {
        '@type': 'Offer',
        'price': guitar.price,
        'priceCurrency': 'TRY',
        'availability': (guitar.stock || 0) > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        'seller': {
          '@type': 'Organization',
          'name': 'Neo Guitar Store'
        }
      },
      ...(guitar.rating && {
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': guitar.rating,
          'reviewCount': guitar.ratingCount || 0
        }
      })
    };
    
    const oldProductScript = document.getElementById('product-schema');
    if (oldProductScript) {
      oldProductScript.remove();
    }
    
    const productScript = document.createElement('script');
    productScript.id = 'product-schema';
    productScript.type = 'application/ld+json';
    productScript.text = JSON.stringify(productSchema);
    document.head.appendChild(productScript);
    
    return () => {
      const breadcrumbScript = document.getElementById('breadcrumb-schema');
      const productScript = document.getElementById('product-schema');
      if (breadcrumbScript) breadcrumbScript.remove();
      if (productScript) productScript.remove();
    };
  }, [guitar]);
  
  return null;
}

function Layout({ children, hideNavbar = false, hideFooter = false }) {
  return (
    <>
      {!hideNavbar && <Navbar />}
      {children}
      {!hideFooter && <Footer />}
    </>
  );
}

export default function Yonlendirme() {
  return (
    <Router>
      <DomainRedirect />
      <MetaTagManager />
      <ScrollWithSpinner>
        <Routes>
          {/* Admin Login - Public */}
          <Route path="/admin/login" element={
            <Layout hideNavbar hideFooter>
              <AdminLogin />
            </Layout>
          } />
          
          {/* Admin Panel - Protected route */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />
          
          <Route path="/admin/*" element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          } />
          
          {/* Gitar Detay Sayfası */}
          <Route path="/gitar/:id" element={
            <Layout>
              <GitarDetay />
            </Layout>
          } />
          
          {/* Ana Sayfa */}
          <Route path="/" element={
            <Layout>
              <App />
            </Layout>
          } />
          
          {/* Gitarlar Sayfası */}
          <Route path="/gitarlar" element={
            <Layout>
              <Gitarlar />
            </Layout>
          } />
          
          {/* Sipariş Sayfası */}
          <Route path="/siparis" element={
            <Layout>
              <Siparis />
            </Layout>
          } />
		  
          {/* Hakkımızda Sayfası - Yeni Eklendi */}
          <Route path="/hakkimizda" element={
            <Layout>
              <Hakkimizda />
            </Layout>
          } />
          
          {/* 404 Not Found */}
          <Route path="*" element={
            <Layout>
              <NotFound404 />
            </Layout>
          } />
        </Routes>
      </ScrollWithSpinner>
    </Router>
  );
}
